/**
 * Sourcito design tokens — single source of truth.
 *
 * Token values are "R G B" space-separated triples so they slot into Tailwind's
 * `rgb(var(--token) / <alpha-value>)` pattern. Adding a new theme = adding a new
 * object below + a new class block in tokens.css.
 */

export const warmDark = {
  bg: {
    canvas: '20 17 15', // #14110f — warm near-black, NOT cool blue-black
    surface: '28 24 21', // #1c1815
    surfaceElev: '37 33 25', // #252119
    overlay: '0 0 0',
  },
  fg: {
    primary: '245 239 230', // #f5efe6
    secondary: '165 155 142', // #a59b8e
    tertiary: '107 98 88', // #6b6258
    onAccent: '20 17 15', // #14110f — text on terracotta
  },
  border: {
    subtle: '42 37 31', // #2a251f
    default: '58 51 41', // #3a3329
  },
  accent: {
    primary: '226 116 92', // #e2745c — terracotta
    primaryHover: '237 130 104', // #ed8268
    primaryPressed: '201 95 72', // #c95f48
    fg: '240 168 146', // #f0a892 — accent rendered as text on neutral bg
  },
  positive: '109 191 155', // #6dbf9b — warm-green for high-ROI
  warning: '212 163 92', // #d4a35c — amber for mid-ROI
  negative: '217 105 89', // #d96959 — warm red, distinct from accent
  info: '111 155 209', // #6f9bd1
  anchor: '245 239 230', // #f5efe6 — same as fg-primary; stays high-contrast in dark
} as const;

export const warmLight = {
  bg: {
    canvas: '250 246 240', // #faf6f0 — warm cream
    surface: '255 255 255', // #ffffff
    surfaceElev: '246 241 234', // #f6f1ea
    overlay: '20 17 15',
  },
  fg: {
    primary: '26 22 20', // #1a1614
    secondary: '107 98 88', // #6b6258
    tertiary: '165 155 142', // #a59b8e
    onAccent: '255 255 255', // #ffffff
  },
  border: {
    subtle: '239 232 222', // #efe8de
    default: '226 216 203', // #e2d8cb
  },
  accent: {
    primary: '201 95 72', // #c95f48
    primaryHover: '184 80 58', // #b8503a
    primaryPressed: '164 69 49', // #a44531
    fg: '201 95 72', // #c95f48
  },
  positive: '47 138 100', // #2f8a64
  warning: '168 123 58', // #a87b3a
  negative: '217 72 94', // #d9485e
  info: '122 150 240', // #7a96f0
  anchor: '25 25 25', // #191919
} as const;

export type Theme = typeof warmDark;

export const themes = {
  'warm-dark': warmDark,
  'warm-light': warmLight,
} as const;

export type ThemeName = keyof typeof themes;
export const DEFAULT_THEME: ThemeName = 'warm-light';
